// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t524 · B-2260 · EL TIPO DE NEGOCIO DE LA CLÍNICA EN LAS PANTALLAS (27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El catálogo de los tipos es el MISMO que lee el servidor (shared/tipos-negocio.json; backend/services/tiposNegocio.js). La
//  clave de la clínica de quien entra llega en GET /api/config-cliente (`tipo_negocio`), que hooks/useBranding apunta aquí
//  (t667: siempre; ya no hay función «tipos-de-negocio» que encender). Mientras el servidor no ha contestado, la clínica es
//  DENTAL y la pantalla es exactamente la de siempre: equivocarse hacia «dental» deja la ficha como hoy; hacia otro tipo le
//  quitaría su odontograma.
//
//  Lo que decide en la pantalla (lo demás lo decide el servidor: los sistemas con que nace, lo sembrado, LucIA):
//   · las pestañas de la ficha que su tipo no tiene (`seccionesDeLaFicha`: la «Perio» fuera de una fisioterapia);
//   · el componente clínico de la ficha (`componenteClinico`: odontograma, mapa del cuerpo, mapa del pie, o ninguno).
//  El vocabulario (paciente o cliente, gabinete o box) NO va aquí: es una capa del diccionario de idiomas.
import CATALOGO from '../../../shared/tipos-negocio.json' with { type: 'json' }
// El vocabulario de cada tipo (paciente o cliente, gabinete o box) es una capa del diccionario de idiomas (t523, docs/
// IDIOMAS-PLAN.md · «Variantes por tipo de negocio»): aquí solo se le dice el tipo. En la dental, ninguno (el de siempre).
import { ponerTipoNegocio as ponerTipoDelVocabulario } from './i18n.js'
// t599 · B-2260 · y en una clínica de animales (ficha.animal: la veterinaria), la ficha siempre representada por su titular.
import { ponerSiempreRepresentado } from './edad.js'

export const TIPOS = Object.freeze(CATALOGO.tipos.map((t) => Object.freeze({ ...t })))
export const POR_DEFECTO = CATALOGO.por_defecto || 'dental'
const POR_CLAVE = new Map(TIPOS.map((t) => [t.clave, t]))

export const tipoDe = (clave) => POR_CLAVE.get(String(clave || '')) || null
export const perfilDe = (clave) => tipoDe(clave) || POR_CLAVE.get(POR_DEFECTO)
export const tiposListos = () => TIPOS.filter((t) => t.listo === true)
export const esDental = (clave) => perfilDe(clave).clave === POR_DEFECTO

// ── LO QUE DICE EL SERVIDOR DE LA CLÍNICA DE QUIEN ENTRA ─────────────────────────────────────────
let actual = POR_DEFECTO
const oyentes = new Set()

export function ponerTipoNegocio(clave) {
  const nuevo = tipoDe(clave) ? String(clave) : POR_DEFECTO
  if (nuevo === actual) return actual
  actual = nuevo
  try { ponerTipoDelVocabulario(nuevo === POR_DEFECTO ? null : nuevo) } catch (_) { /* sin el diccionario: las palabras de siempre */ }
  try { ponerSiempreRepresentado(((perfilDe(nuevo) || {}).ficha || {}).animal === true) } catch (_) { /* sin la regla: la de la edad */ }
  for (const f of [...oyentes]) { try { f() } catch (_) { /* un oyente roto no para a los demás */ } }
  return actual
}
export const tipoNegocioActual = () => actual
export const perfilActual = () => perfilDe(actual)
export function suscribirTipoNegocio(f) { oyentes.add(f); return () => { oyentes.delete(f) } }

// ── LO QUE DECIDE EN LA FICHA (puras: las mide la guardia sin montar nada) ──────────────────────
// Las pestañas de la ficha que ve este tipo: las de siempre menos las que su tipo no tiene. En la dental, todas, sin tocar.
export function seccionesDeLaFicha(secciones, perfil = perfilActual()) {
  const fuera = new Set((perfil && perfil.ficha && Array.isArray(perfil.ficha.pestanas_fuera)) ? perfil.ficha.pestanas_fuera : [])
  if (!fuera.size) return secciones
  return (secciones || []).filter((s) => !fuera.has(s.id))
}
// Las pestañas que un tipo no tiene (las ids de la ficha: «perio»...). Un tipo que no existe, o la dental, ninguna.
export function pestanasFueraDe(clave) {
  const p = tipoDe(clave)
  return (p && p.ficha && Array.isArray(p.ficha.pestanas_fuera)) ? p.ficha.pestanas_fuera.slice() : []
}
// t524 · EL EJEMPLO DE UN CUADRO DE TEXTO («p.ej. Ortodoncia») que es de su tipo: el de shared/tipos-negocio.json
// (`ejemplos`) o, si su tipo no lo tiene (la dental, o la función apagada), el de siempre, que es el segundo argumento.
export function ejemplo(clave, deSiempre, perfil = perfilActual()) {
  const e = perfil && perfil.ejemplos ? perfil.ejemplos[clave] : null
  return (typeof e === 'string' && e.trim()) ? e : deSiempre
}
// t524 · B-2260 · un tratamiento de ejemplo de su tipo: el primero de su catálogo de partida que no es la primera visita (lo que
// enseñan la vista previa de una plantilla y su lista de variables). En la dental, `deSiempre` («Implante dental»).
export function tratamientoDeEjemplo(deSiempre, perfil = perfilActual()) {
  if (!perfil || perfil.clave === POR_DEFECTO) return deSiempre
  const cat = (perfil.siembra && Array.isArray(perfil.siembra.catalogo)) ? perfil.siembra.catalogo : []
  const f = cat.find((x) => x && x[0] && !/^primera/i.test(String(x[0]))) || cat[0]
  return (f && f[0]) ? String(f[0]) : deSiempre
}
// El componente clínico de la ficha: 'odontograma' (dental), 'mapa_corporal', 'mapa_pie' o 'ninguno'.
export function componenteClinico(perfil = perfilActual()) {
  return (perfil && perfil.ficha && perfil.ficha.componente) || 'odontograma'
}
